import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CustomAmountInput, customInvalidFor } from "@/components/RequestPaymentDialog";

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const OPTIONS = ["Full Payment", "Custom Amount"];

export default function RequestCostPaymentDialog({ open, onOpenChange, cost, onCreated }) {
  const [elig, setElig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState("Full Payment");
  const [customAmt, setCustomAmt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !cost?.id) return;
    setNote(""); setSelected("Full Payment"); setCustomAmt(""); setElig(null); setLoading(true);
    api.get(`/approvals/cost-eligibility`, { params: { cost_id: cost.id } })
      .then(({ data }) => setElig(data))
      .catch(() => setElig({ available: [], suggested_amount: {}, has_pending: false, has_invoice: false }))
      .finally(() => setLoading(false));
  }, [open, cost?.id]);

  const submit = async () => {
    if (!elig?.has_invoice) return;  // button is disabled when has_invoice=false
    setSubmitting(true);
    try {
      await api.post("/approvals/cost", {
        cost_id: cost.id, note, payment_type: selected,
        ...(selected === "Custom Amount" ? { percent: parseFloat(customAmt) } : {}),
      });
      toast.success("Payment request raised");
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to raise request");
    } finally { setSubmitting(false); }
  };

  const amount = Number(elig?.suggested_amount?.["Full Payment"] || 0);
  const invoiceMissing = elig && !elig.has_invoice;
  const total = Number(elig?.cost_amount || cost?.amount || 0);
  const customInvalid = selected === "Custom Amount" && customInvalidFor(customAmt, total, amount);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="request-cost-payment-dialog">
        <DialogHeader>
          <DialogTitle>Request cost payment</DialogTitle>
          <DialogDescription>
            {cost?.cost_type} · Total {fmtCur(cost?.amount)} · Paid {fmtCur(elig?.total_paid)}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
        ) : invoiceMissing ? (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="cost-no-invoice">
            Invoice not available — please upload an invoice and try again.
          </div>
        ) : elig?.has_pending ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            A pending request already exists for this cost. Wait for the admin to release or reject it before raising a new one.
          </div>
        ) : amount <= 0 ? (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">
            Payment complete — nothing left to request.
          </div>
        ) : (
          <div className="space-y-3">
            {OPTIONS.map((pt) => (
              <label key={pt} className={`flex items-center justify-between gap-3 p-3 border rounded-md cursor-pointer transition-colors ${selected === pt ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`payment-type-${pt.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="cost_payment_type" checked={selected === pt} onChange={() => setSelected(pt)} className="accent-blue-600" />
                  <span className="text-sm font-medium text-slate-900">{pt}</span>
                </div>
                <span className="font-mono text-sm font-semibold text-slate-900">{pt === "Full Payment" ? fmtCur(amount) : "Enter %"}</span>
              </label>
            ))}
            {selected === "Custom Amount" && (
              <CustomAmountInput value={customAmt} onChange={setCustomAmt} total={total} remaining={amount} invoiceType={elig?.invoice_type || cost?.gst_type} />
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Note (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                data-testid="cost-request-note"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cost-request-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || invoiceMissing || elig?.has_pending || amount <= 0 || customInvalid}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="cost-request-submit"
          >
            {submitting ? "Submitting…" : "Request payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
